// Ported from relay-protocol packages/relay-widget/src/useRecovery.ts
// (v0.1.0, updated to upstream e5bff75). Mechanical substitution only: the
// wagmi hooks the original consumed (useAccount, useSwitchChain,
// useWriteContract, usePublicClient) are replaced by the injected
// `opts.wallet` functions (RelayWalletProps), and viem's `isAddress` by
// ../lib/units -- dapp-ui may depend on neither wagmi nor viem.
// verifyForwarder's signature grew a leading (readContract, chainId) pair
// instead of taking a wagmi publicClient. Everything else is the original
// logic, untouched.
//
// e5bff75 split the WRITE path out into `useRecoveryActions`, which takes the
// forwarder's info as an ARGUMENT and keys progress by forwarder address --
// because a hook cannot be called once per forwarder in a loop, and the
// recovery panel now renders one section per forwarder (a depositor-kind
// target derives its own forwarder; every swap-kind target shares one -- see
// ../lib/recoveryGroups.ts). `useRecovery` is unchanged for its callers: it is
// now that hook plus a fetch, binding its own single fetched `info` in.
//
// Every recovery tx (factory.deploy, forwarder.execute/sweepToUser) carries
// no destination argument: funds always route to the forwarder's immutable
// recipient, so `info` here is advisory/orientation only, never a gate the
// UI uses to block an action. `info.inFlight` is shown as a caution line but
// never disables a button.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRelayClient, RelayApiError } from "../lib/client";
import type { RelayClient } from "../lib/client";
import type { RecoveryInfo } from "../lib/types";
import { standingForwarderAbi, standingForwarderFactoryAbi } from "../lib/abis";
import { verifyForwarder } from "../lib/verifyForwarder";
import { isAddress } from "../lib/units";
import type { Hex, RecoverySuccessResult, RelayWalletProps } from "../types";

/** Every recovery tx (factory.deploy, forwarder.execute/sweepToUser) is signed and sent on
 * BSC, regardless of which chain the connected wallet started on. */
const BSC_CHAIN_ID = 56;

export type RecoveryAction = "execute" | "sweep";

/** Per-attempt progress for `recover()`. "switching" = prompting a chain switch to BSC;
 * "deploying" = the factory.deploy() write is in flight/confirming (only needed when the
 * forwarder wasn't deployed yet); "recovering" = the execute()/sweepToUser() write is in
 * flight/confirming; "done"/"error" are terminal for this attempt. */
export type RecoveryStep = "idle" | "switching" | "deploying" | "recovering" | "done" | "error";

export interface RecoveryState {
  step: RecoveryStep;
  /** Tx hash of the factory.deploy() write -- only set when this attempt needed one. */
  deployTxHash?: string;
  /** Tx hash of the execute()/sweepToUser() write. */
  actionTxHash?: string;
  error?: string;
}

const IDLE_STATE: RecoveryState = { step: "idle" };

/**
 * Which token a token-less `recover('sweep')` call sweeps (Task 7 back-compat default): the
 * first `info.tokens` entry the server flags `recoverable`, or -- when `tokens` is absent
 * (older backend, or none recoverable) -- the legacy `info.token` (BSC USDT) that pre-Task-6
 * callers always swept. Purely a WHICH-ASSET selection; never affects the recovery
 * destination (see `recover`'s doc below).
 */
function primaryRecoverableToken(info: RecoveryInfo): string {
  return info.tokens?.find((t) => t.recoverable)?.address ?? info.token;
}

export interface UseRecoveryActionsOptions {
  /** The EVM address recovery actions are for -- normally the connected wallet's own
   * address. Funds always route here (the forwarder's immutable recipient), never anywhere
   * else, regardless of which action is taken. */
  recipient?: string;
  /** Injected wallet capabilities (dapp-ui depends on neither wagmi nor viem). A missing
   * writeContract/readContract/waitForReceipt fails `recover()` closed rather than
   * crashing; a missing switchChain only errors when a switch is actually needed. */
  wallet?: RelayWalletProps;
  /** Overrides the pinned set of factories `recover()` will sign against
   * (security report L-003/L-008) -- only needed by a self-hosted deployment
   * running its own factories. Defaults to DEFAULT_TRUSTED_FACTORIES.
   * Deliberately NOT sourced from the API (see verifyForwarder.ts). */
  trustedFactories?: readonly string[];
  onError?: (error: Error) => void;
  /** Called once after the action receipt confirms successfully. */
  onRecoverySuccess?: (result: RecoverySuccessResult) => void;
  /** Called after a recovery attempt changes on-chain state (a completed deploy, a
   * completed action), so the owner of the recovery data can refetch it. */
  onChanged?: () => void;
}

export interface UseRecoveryActionsResult {
  /**
   * Signs the requested recovery tx for ONE specific forwarder, taking that
   * forwarder's info explicitly rather than closing over a single selected one.
   *
   * That signature is what lets the panel render a section per forwarder: a
   * deployment's targets can resolve to several distinct forwarders (a
   * depositor-kind target derives its own), each holding its own balance and
   * needing its own button, and a hook cannot be called once per forwarder in a
   * loop. So the WRITE path takes the info as an argument and the state is keyed
   * by forwarder -- see stateFor.
   *
   * `tokenAddress` selects WHICH asset a "sweep" recovers -- passed straight
   * through as sweepToUser's sole argument. It does NOT affect where funds go:
   * every recovery tx carries no destination argument and always routes to the
   * forwarder's immutable recipient. Omitted: defaults to the primary
   * recoverable token from `info.tokens`, falling back to the legacy
   * `info.token` (BSC USDT).
   */
  recover: (info: RecoveryInfo, action: RecoveryAction, tokenAddress?: string) => Promise<void>;
  /** This forwarder's own attempt progress, so one section's spinner and error
   * never appear under another's balance. Idle for a forwarder never acted on. */
  stateFor: (forwarder: string | undefined) => RecoveryState;
  /** True while ANY forwarder's attempt is in flight. Buttons are disabled
   * globally during one: the wallet can only work on one transaction at a time,
   * so offering a second is offering a queue the user did not ask for. */
  busy: boolean;
}

/**
 * The write half of the recovery flow: chain switch, pre-signing address
 * verification, optional factory.deploy, then execute()/sweepToUser() -- for
 * whichever forwarder the caller names.
 *
 * Split out of useRecovery (upstream e5bff75) so several forwarders can be
 * acted on from one render. useRecovery still exists and is unchanged for
 * callers that only care about one target; it is now this hook plus a fetch.
 */
export function useRecoveryActions(opts: UseRecoveryActionsOptions = {}): UseRecoveryActionsResult {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const wallet = opts.wallet;
  const connectedChainId = wallet?.walletChainId;

  const recipient = opts.recipient;
  const recipientKey = recipient && isAddress(recipient) ? recipient : undefined;

  // Keyed by LOWERCASED forwarder address: the API's checksum casing is not
  // guaranteed stable across responses, and a casing difference must not orphan
  // an in-flight attempt's progress from the section that started it.
  const [states, setStates] = useState<Record<string, RecoveryState>>({});

  const setState = useCallback((forwarder: string, next: RecoveryState) => {
    setStates((prev) => ({ ...prev, [forwarder.toLowerCase()]: next }));
  }, []);

  const stateFor = useCallback(
    (forwarder: string | undefined) => (forwarder ? (states[forwarder.toLowerCase()] ?? IDLE_STATE) : IDLE_STATE),
    [states],
  );

  const busy = useMemo(
    () =>
      Object.values(states).some(
        (s) => s.step === "switching" || s.step === "deploying" || s.step === "recovering",
      ),
    [states],
  );

  const recover = useCallback(
    async (info: RecoveryInfo, action: RecoveryAction, tokenAddress?: string) => {
      if (!recipientKey || !info?.forwarder) return;
      const key = info.forwarder;

      setState(key, { step: "switching" });
      try {
        if (connectedChainId !== BSC_CHAIN_ID) {
          if (!wallet?.switchChain) {
            throw new Error("wallet cannot switch chains");
          }
          await wallet.switchChain(BSC_CHAIN_ID);
        }

        if (!wallet?.writeContract || !wallet?.readContract || !wallet?.waitForReceipt) {
          throw new Error("wallet is missing contract capabilities required for recovery");
        }

        // Independent address verification BEFORE any signature (security
        // report L-003/L-008). Both writes below aim at addresses that arrived
        // over HTTP -- info.factory and info.forwarder -- so this proves,
        // against a pinned factory list and the chain itself (via the user's
        // own RPC, not our backend), that the forwarder really is the one this
        // factory deploys for THIS recipient.
        //
        // Placed after the chain switch so the read happens on BSC, and before
        // the deploy write so a bad response costs the user nothing at all.
        // Fails closed: an unverifiable address aborts instead of signing.
        const verdict = await verifyForwarder(wallet.readContract, BSC_CHAIN_ID, {
          factory: info.factory,
          forwarder: info.forwarder,
          // recipientKey, not a field from the response: the point is to prove
          // the forwarder belongs to the account we are acting for.
          recipient: recipientKey,
          version: info.version,
          // optsRef, not opts: this callback's dep list deliberately omits
          // option fields (same reason onError is read through the ref), so a
          // direct `opts.` read here would capture a stale list.
          trusted: optsRef.current.trustedFactories,
        });
        if (!verdict.ok) {
          throw new Error(`address verification failed: ${verdict.reason}`);
        }

        let deployTxHash: string | undefined;

        // Permissionless deploy: any address can call factory.deploy(recipient, version) for
        // any recipient -- it always resolves to the same CREATE2 address, so this is safe to
        // (re)issue even if another actor deployed it in the meantime (the tx would just be a
        // no-op revert, not a fund-routing risk either way, since deploy takes no funds).
        if (!info.isDeployed) {
          setState(key, { step: "deploying" });
          deployTxHash = await wallet.writeContract({
            address: info.factory as Hex,
            abi: standingForwarderFactoryAbi,
            functionName: "deploy",
            args: [recipientKey as Hex, BigInt(info.version)],
            chainId: BSC_CHAIN_ID,
          });
          setState(key, { step: "deploying", deployTxHash });
          // The receipt's STATUS decides, not its existence (upstream 07354b7).
          // waitForReceipt resolves for a reverted tx exactly as it does for a
          // successful one, so discarding the return value reported every
          // revert as a completed recovery -- the user saw "done" while the
          // forwarder was never deployed and their funds had not moved. A
          // reverted deploy also stops HERE, before the action write, which
          // would otherwise spend a second fee on a call that cannot succeed.
          const deployReceipt = await wallet.waitForReceipt({ chainId: BSC_CHAIN_ID, hash: deployTxHash as Hex });
          if (deployReceipt.status === "reverted") {
            throw new Error(`forwarder deploy reverted on-chain (tx ${deployTxHash})`);
          }
          // The forwarder now exists on-chain. Tell the owner so info.isDeployed
          // re-reads true: if the action write below fails and the user retries,
          // we must NOT re-issue deploy() (it would revert against the
          // already-deployed CREATE2 address). This attempt continues on the
          // stable `info` argument; the refetch only affects retries.
          optsRef.current.onChanged?.();
        }

        setState(key, { step: "recovering", deployTxHash });
        const actionTxHash =
          action === "execute"
            ? await wallet.writeContract({
                address: info.forwarder as Hex,
                abi: standingForwarderAbi,
                functionName: "execute",
                args: [],
                chainId: BSC_CHAIN_ID,
              })
            : await wallet.writeContract({
                address: info.forwarder as Hex,
                abi: standingForwarderAbi,
                functionName: "sweepToUser",
                args: [(tokenAddress ?? primaryRecoverableToken(info)) as Hex],
                chainId: BSC_CHAIN_ID,
              });

        setState(key, { step: "recovering", deployTxHash, actionTxHash });
        const actionReceipt = await wallet.waitForReceipt({ chainId: BSC_CHAIN_ID, hash: actionTxHash as Hex });
        if (actionReceipt.status === "reverted") {
          // Thrown, so it lands in the catch below and surfaces as step 'error'
          // with the tx hash in the message. Reporting 'done' here would tell a
          // user their stuck funds had been recovered when nothing moved.
          throw new Error(`recovery transaction reverted on-chain (tx ${actionTxHash})`);
        }

        setState(key, { step: "done", deployTxHash, actionTxHash });
        optsRef.current.onRecoverySuccess?.({
          action,
          forwarder: info.forwarder,
          actionTxHash,
          ...(deployTxHash ? { deployTxHash } : {}),
        });
        optsRef.current.onChanged?.();
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setStates((prev) => {
          const k = key.toLowerCase();
          return { ...prev, [k]: { ...(prev[k] ?? IDLE_STATE), step: "error", error: e.message } };
        });
        optsRef.current.onError?.(e);
      }
    },
    [recipientKey, connectedChainId, wallet, setState],
  );

  return { recover, stateFor, busy };
}

export interface UseRecoveryOptions {
  /** Pre-built client. Takes precedence over apiBaseUrl. */
  client?: RelayClient;
  /** Used to build a client via createRelayClient when `client` isn't passed. */
  apiBaseUrl?: string;
  /** The EVM address recovery status/actions are for -- normally the connected wallet's own
   * address. Funds always route here (the forwarder's immutable recipient), never anywhere
   * else, regardless of which action is taken. */
  recipient?: string;
  /**
   * The currently selected CROSS delivery target symbol -- threaded into
   * GET /v1/recovery's "?target=" so a depositor-kind (e.g. pONEUSD) target reports
   * ITS OWN forwarder/factory instead of the default swap one. Omitted/undefined selects the
   * configured default, byte-identical to pre-multi-target behavior.
   */
  target?: string;
  /** Gate on the fetch (default true). A caller that already fetched every target's
   * recovery info (RecoverFunds in per-forwarder mode) disables this hook's own
   * request rather than re-requesting what it just received (upstream e5bff75). */
  enabled?: boolean;
  /** Optional automatic REST refresh cadence in milliseconds. Disabled when omitted or <= 0. */
  refetchInterval?: number;
  /**
   * Injected wallet capabilities (dapp-ui depends on neither wagmi nor viem, so
   * the host supplies these). Every one that's missing degrades gracefully --
   * no `walletChainId` means "unknown current chain", and a missing
   * writeContract/readContract/waitForReceipt fails `recover()` closed rather
   * than crashing the widget.
   */
  wallet?: RelayWalletProps;
  /** Called when a GET /v1/recovery request 401s. */
  onUnauthorized?: () => void;
  onError?: (error: Error) => void;
  /** Called once after the action receipt confirms successfully. */
  onRecoverySuccess?: (result: RecoverySuccessResult) => void;
  /** See UseRecoveryActionsOptions.trustedFactories. */
  trustedFactories?: readonly string[];
}

export interface UseRecoveryResult {
  info?: RecoveryInfo;
  loading: boolean;
  error?: Error;
  /** Re-fetches GET /v1/recovery for the current recipient (also called automatically once a
   * recovery attempt completes, so the panel reflects the new on-chain state). */
  refresh: () => void;
  /** Signs the requested recovery tx for this hook's own fetched `info` -- see
   * UseRecoveryActionsResult.recover for semantics. */
  recover: (action: RecoveryAction, tokenAddress?: string) => Promise<void>;
  recoveryState: RecoveryState;
}

export function useRecovery(opts: UseRecoveryOptions = {}): UseRecoveryResult {
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const client = useMemo(() => {
    if (opts.client) return opts.client;
    if (!opts.apiBaseUrl) {
      throw new Error("useRecovery: either `client` or `apiBaseUrl` must be provided");
    }
    return createRelayClient({ baseUrl: opts.apiBaseUrl });
  }, [opts.client, opts.apiBaseUrl]);

  const recipient = opts.recipient;
  const recipientKey = recipient && isAddress(recipient) ? recipient : undefined;

  // ---- GET /v1/recovery -------------------------------------------------
  const [info, setInfo] = useState<RecoveryInfo>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const [refreshNonce, setRefreshNonce] = useState(0);

  const target = opts.target;
  const enabled = opts.enabled ?? true;
  const refetchInterval = opts.refetchInterval;

  useEffect(() => {
    if (!recipientKey || !enabled) {
      setInfo(undefined);
      setError(undefined);
      setLoading(false);
      return;
    }

    let cancelled = false;
    let inFlight = false;
    let isFirstFetch = true;

    async function refetchRecovery() {
      if (cancelled || inFlight) return;
      inFlight = true;
      if (isFirstFetch) setLoading(true);
      try {
        const result = await client.getRecovery({ user: recipientKey as string, target });
        if (cancelled) return;
        setInfo(result);
        setError(undefined);
      } catch (err) {
        if (cancelled) return;
        const e = err instanceof Error ? err : new Error(String(err));
        setError(e);
        optsRef.current.onError?.(e);
        if (e instanceof RelayApiError && e.status === 401) {
          optsRef.current.onUnauthorized?.();
        }
      } finally {
        inFlight = false;
        if (!cancelled && isFirstFetch) setLoading(false);
        isFirstFetch = false;
      }
    }

    void refetchRecovery();
    const refetchTimer =
      typeof refetchInterval === "number" &&
      Number.isFinite(refetchInterval) &&
      refetchInterval > 0
        ? setInterval(() => {
            void refetchRecovery();
          }, refetchInterval)
        : undefined;

    return () => {
      cancelled = true;
      if (refetchTimer) clearInterval(refetchTimer);
    };
    // refreshNonce is a write-only trigger -- bumping it re-runs this fetch.
  }, [client, recipientKey, refreshNonce, target, enabled, refetchInterval]);

  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  // ---- recover(action) -------------------------------------------------
  // The write path itself now lives in useRecoveryActions (which takes the
  // forwarder's info as an ARGUMENT so a caller can drive several forwarders
  // from one render). This hook is the single-target binding onto it: it feeds
  // its own fetched `info` in, and reports back only that forwarder's state.
  const actions = useRecoveryActions({
    recipient: opts.recipient,
    wallet: opts.wallet,
    trustedFactories: opts.trustedFactories,
    onError: opts.onError,
    onRecoverySuccess: opts.onRecoverySuccess,
    onChanged: refresh,
  });

  const recover = useCallback(
    async (action: RecoveryAction, tokenAddress?: string) => {
      if (!info) return;
      await actions.recover(info, action, tokenAddress);
    },
    [actions, info],
  );

  const recoveryState = actions.stateFor(info?.forwarder);

  return { info, loading, error, refresh, recover, recoveryState };
}
